/**
 * Single source of truth for the admin panel sections (spec 028).
 *
 * `NAV_GROUPS` is the drawer menu of `Home.vue`; the same list feeds the
 * "Sección de inicio" selector of the Profile, so a section can only be chosen
 * if it is in the menu. Each leaf has a stable `key`: the admin's preference
 * stores the key (backend `users-admin.preferences.defaultSection`), never the
 * route, so a route can move without breaking saved preferences. Keys must stay
 * unique lowercase slugs (the backend rejects anything else) and must never be
 * renamed once released — renaming one silently resets that preference.
 *
 * When per-admin permissions exist, filter `getSelectableSections()` and the
 * menu with the same rule.
 */

export const NAV_GROUPS = [
  {
    title: 'Métricas',
    icon: 'mdi-chart-line',
    subLinks: [
      {
        key: 'mrr-resume',
        link: '/finances/metas',
        title: 'MRR Resumen',
        icon: 'mdi-target'
      },
      {
        key: 'mrr-breakdown',
        link: '/finances/mrr-detalle',
        title: 'MRR Breakdown',
        icon: 'mdi-table-search'
      },
      {
        key: 'mrr-new',
        link: '/metricas/new-mrr',
        title: 'MRR New',
        icon: 'mdi-trending-up'
      },
      {
        key: 'mrr-b2c-b2b2c',
        link: '/finances/b2c-report-v2',
        title: 'MRR B2C / B2B2C',
        icon: 'mdi-account-group-outline'
      },
      {
        key: 'gross-margin',
        link: '/metricas/contract-profitability',
        title: 'Gross Margin',
        icon: 'mdi-cash-multiple'
      },
      {
        key: 'cac-cltv',
        link: '/metricas/cac-cltv',
        title: 'B2B CLTV',
        icon: 'mdi-account-cash-outline'
      },
      {
        key: 'b2c-cltv',
        link: '/metricas/b2c-cltv',
        title: 'B2C CLTV',
        icon: 'mdi-account-heart-outline'
      },
      {
        key: 'pet-protection-metrics',
        link: '/metricas/mascotas-protegidas',
        title: 'Mascotas protegidas',
        icon: 'mdi-paw'
      },
      {
        key: 'customer-success',
        link: '/metricas/contract-success',
        title: 'Customer Success',
        icon: 'mdi-trophy-outline'
      },
      {
        key: 'refund-metrics',
        link: '/metricas/reembolsos',
        title: 'Reembolsos',
        icon: 'mdi-cash-refund'
      },
      {
        key: 'petmed-metrics',
        link: '/metricas/petmed',
        title: 'PetMed',
        icon: 'mdi-stethoscope'
      },
      {
        key: 'sponsorship-metrics',
        link: '/sponsorships/metricas',
        title: 'Apadrinamiento',
        icon: 'mdi-chart-line'
      },
      {
        key: 'ces-metrics',
        link: '/metricas/ces',
        title: 'CES',
        icon: 'mdi-emoticon-happy-outline'
      },
    ]
  },
  {
    title: 'General',
    icon: 'mdi-view-dashboard',
    open: true,
    subLinks: [
      {
        key: 'companies',
        link: '/business-relationships/companies',
        title: 'Empresas',
        icon: 'mdi-office-building'
      },
      {
        key: 'contracts',
        link: '/contratos',
        title: 'Contratos',
        icon: 'mdi-file-document-outline'
      },
      {
        key: 'plans',
        link: '/business-relationships/plans',
        title: 'Planes',
        icon: 'mdi-account-multiple'
      },
      {
        key: 'users',
        link: '/users',
        title: 'Usuarios',
        icon: 'mdi-account-group'
      },
      {
        key: 'co-tutors',
        link: '/vetops/co-tutores',
        title: 'Co-tutores',
        icon: 'mdi-account-multiple-plus'
      },
      {
        key: 'pets',
        link: '/pets',
        title: 'Mascotas',
        icon: 'mdi-paw'
      },
      {
        key: 'memberships',
        link: '/memberships',
        title: 'Membresías',
        icon: 'mdi-badge-account-horizontal'
      },
      {
        key: 'my-downloads',
        link: '/my-downloads',
        title: 'Mis Descargas',
        icon: 'mdi-tray-arrow-down'
      },
      {
        key: 'arco-requests',
        link: '/opposition-requests',
        title: 'Solicitudes ARCO+',
        icon: 'mdi-shield-account-outline'
      },
      {
        key: 'shelter-pets',
        link: '/sponsorships/shelter-pets',
        title: 'Apadrinamiento',
        icon: 'mdi-heart'
      },
      {
        key: 'discount-club',
        link: '/business-relationships/alliances',
        title: 'Club de Descuentos',
        icon: 'mdi-tag-multiple'
      },
      {
        key: 'poster-ads',
        link: '/business-relationships/poster-ads',
        title: 'Posters de beneficios',
        icon: 'mdi-view-carousel-outline'
      }
    ]
  },
  {
    title: 'Revenue',
    icon: 'mdi-podium',
    subLinks: [
      {
        key: 'client-org-charts',
        link: '/clients/org-charts',
        title: 'Organigramas',
        icon: 'mdi-sitemap'
      },
      {
        key: 'b2b-renewals',
        link: '/metricas/renovaciones',
        title: 'B2B Renovaciones',
        icon: 'mdi-calendar-refresh'
      },
      {
        key: 'b2c-renewals',
        link: '/vencimientos',
        title: 'B2C Renovaciones',
        icon: 'mdi-calendar-alert-outline'
      },
      {
        key: 'plan-ranking',
        link: '/business-relationships/plans/ranking',
        title: 'Ranking de planes',
        icon: 'mdi-podium'
      },
      {
        key: 'contract-ranking',
        link: '/contratos/ranking',
        title: 'Ranking de Contratos',
        icon: 'mdi-file-chart'
      },
      {
        key: 'quote-builder',
        link: '/cotizador',
        title: 'Cotizador',
        icon: 'mdi-calculator-variant-outline'
      },
      {
        key: 'quotes',
        link: '/cotizaciones',
        title: 'Cotizaciones',
        icon: 'mdi-file-document-multiple-outline'
      },
    ]
  },
  {
    title: 'VetOps',
    icon: 'mdi-medical-bag',
    subLinks: [
      {
        key: 'refund-inbox',
        link: '/refunds/new',
        title: 'Inbox de Reembolsos',
        icon: 'mdi-inbox-arrow-down'
      },
      {
        key: 'register-refund',
        link: '/registers/register-refunds',
        title: 'Registrar Reembolso',
        icon: 'mdi-magnify'
      },
      {
        key: 'refund-clinics',
        link: '/refunds/clinics',
        title: 'Clínicas de reembolso',
        icon: 'mdi-hospital-building'
      },
      {
        key: 'refund-redirect-profiles',
        link: '/refunds/redirect-profiles',
        title: 'Redirección de reembolsos',
        icon: 'mdi-call-split'
      },
      {
        key: 'register-service',
        link: '/registers/servicesrecords',
        title: 'Registrar Servicio',
        icon: 'mdi-plus-circle-outline'
      },
      {
        key: 'droopy-clinics',
        link: '/clinics/droopy',
        title: 'Droopy',
        icon: 'mdi-hospital-building'
      }
    ]
  },
  {
    title: 'Operaciones',
    icon: 'mdi-cog-sync',
    subLinks: [
      {
        key: 'bulk-uploads',
        link: '/cargas-masivas',
        title: 'Cargas Masivas',
        icon: 'mdi-table-arrow-up'
      },
      {
        key: 'contract-renewal',
        link: '/renovacion-contrato',
        title: 'Renovación de Contrato',
        icon: 'mdi-file-swap-outline'
      },
      {
        key: 'terms-notification',
        link: '/terms-notification',
        title: 'Términos y Condiciones',
        icon: 'mdi-file-document-edit-outline'
      }
    ]
  },
  {
    title: 'Finanzas',
    icon: 'mdi-bank',
    subLinks: [
      {
        key: 'actuarial-analysis',
        link: '/finanzas/analisis-actuarial',
        title: 'Análisis actuarial',
        icon: 'mdi-chart-bell-curve-cumulative'
      },
      {
        key: 'mrr-billing-entry',
        link: '/finances/billing/individual',
        title: 'Registro de MRR',
        icon: 'mdi-file-plus-outline'
      },
      {
        key: 'mrr-billing-import',
        link: '/finances/billing/importar',
        title: 'Importar MRR',
        icon: 'mdi-table-arrow-up'
      },
      {
        key: 'service-ranking',
        link: '/metricas/service-ranking',
        title: 'Ranking de servicios',
        icon: 'mdi-medical-bag'
      },
      {
        key: 'clinic-billing',
        link: '/finanzas/facturacion-clinicas',
        title: 'Facturación de Clínicas',
        icon: 'mdi-receipt-text-outline'
      }
    ]
  },
  {
    title: 'Mantenedores',
    icon: 'mdi-cog',
    subLinks: [
      {
        key: 'products',
        link: '/contratos/productos',
        title: 'Productos',
        icon: 'mdi-package-variant-closed'
      },
      {
        key: 'brokers',
        link: '/configuracion/brokers',
        title: 'Brokers',
        icon: 'mdi-handshake'
      },
      {
        key: 'admins',
        link: '/configuracion/ejecutivos',
        title: 'Administradores',
        icon: 'mdi-shield-account'
      },
      {
        key: 'exchange-rate',
        link: '/finances/tipo-cambio',
        title: 'Tasa de Cambio',
        icon: 'mdi-currency-usd'
      },
      {
        key: 'refund-reply-templates',
        link: '/mantenedores/respuestas-tipo',
        title: 'Respuestas para reembolsos',
        icon: 'mdi-text-box-multiple-outline'
      },
      {
        key: 'services',
        link: '/mantenedores/servicios',
        title: 'Servicios',
        icon: 'mdi-cog-box'
      },
      {
        key: 'business-lines',
        link: '/mantenedores/lineas-negocio',
        title: 'Líneas de negocio',
        icon: 'mdi-sitemap'
      }
    ]
  },
  {
    title: 'Herramientas Internas',
    icon: 'mdi-toolbox-outline',
    subLinks: [
      {
        key: 'ranger-test-chat',
        link: '/herramientas-internas/ranger-test-chat',
        title: 'Chat de prueba Ranger',
        icon: 'mdi-robot-outline'
      }
    ]
  }
];

/** Key of the section used when the admin has no (valid) preference: MRR Resumen. */
export const DEFAULT_SECTION_KEY = 'mrr-resume';

/**
 * Flat list of the sections an admin can pick as their landing.
 * @returns {{ key: string, link: string, title: string, icon: string, group: string }[]}
 */
export function getSelectableSections() {
  return NAV_GROUPS.flatMap((group) =>
    (group.subLinks || []).map(({ key, link, title, icon }) => ({
      key,
      link,
      title,
      icon,
      group: group.title,
    }))
  );
}

/**
 * Finds a selectable section by its key.
 * @param {string|null|undefined} key
 * @returns {{ key: string, link: string, title: string, icon: string, group: string }|null}
 */
export function findSection(key) {
  if (!key) return null;
  return getSelectableSections().find((section) => section.key === key) || null;
}

/** Route of the default section (MRR Resumen, `/finances/metas`). */
export const DEFAULT_LANDING = findSection(DEFAULT_SECTION_KEY).link;

/**
 * Route the admin lands on when entering the panel (login or `/`).
 * An unknown, removed or missing key falls back to MRR Resumen, never to an error.
 * @param {string|null|undefined} key - `preferences.defaultSection` from `/me`.
 * @returns {string}
 */
export function resolveLanding(key) {
  const section = findSection(key);
  return section ? section.link : DEFAULT_LANDING;
}
